import {
  FC,
  useCallback,
  useState,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import {
  Cancel,
  ArrowCircleLeft,
  ArrowCircleRight,
  ArrowForwardIos,
} from '@mui/icons-material';
import GroupsIcon from '@mui/icons-material/Groups';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { v4 as uuidv4 } from 'uuid';
import AIMinisterIcon from '@assets/aiminister-icon.png';
import { useNavigate } from 'react-router-dom';

import Tabs from '@components/Tabs';
import {
  SelectSingle,
  SelectMultiple,
  SelectCountry,
} from '@components/Input/Select';

import OccupationTab from './tabs/OccupationTab';
import LabourDemandPulseTab from './tabs/LabourDemandPulseTab';
import EducationCareerTab from './tabs/EducationCareerTab';
import HeatMapTab from './tabs/HeatMapTab';
import CountryFlag from './components/CountryFlag';
import { Cancelled } from '@api/request';
import * as url from '@api/requests';
import * as types from '@models/types';
import { DefaultTimelines, Timelines } from '@config/timelines';

import { GlobeContext } from '@providers/Globe';
import { CountriesContext } from '@providers/Countries';
import { ProfileContext } from '@providers/Profile';
import { EducationCareerContext } from '@providers/EducationCareer';
import { OccupationsContext } from '@providers/Occupations';
import { LabourDemandPulseContext } from '@providers/LabourDemandPulse';
import { HeatMapContext } from '@providers/HeatMap';

import useViewportChange from '@helpers/useViewportChange';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import './CountryDetails.scss';
import { getFullAccessHubIds } from '../../helpers/utils';
import ChatModal from '@components/ChatModal';
import { ChatBotContext } from '@providers/ChatBot';
import Typewriter from '@components/ChatModal/Typewriter';
import ISCOArchetypesComponent from './components/ISCOArchetypesComponent';
import { ISCOArchetypeContext } from '@providers/ISCOArchetype';
import LaborForecast from './components/laborForcast';

type CountryDetailsProps = { onClose: () => void };

const CountryDetails: FC<CountryDetailsProps> = ({ onClose }) => {
  const { isMobile } = useViewportChange();
  const [isOpenChatModal, setIsOpenChatModal] = useState<boolean>(false);
  const [isChatHovered, setIsChatHovered] = useState<boolean>(false);
  const { selectedCountry, setSelectedCountry, setGlobePosition } =
    useContext(GlobeContext);
  const { countryDetails } = useContext(CountriesContext);
  const { setTempRequestSearch, conversationId, setConversationId } =
    useContext(ChatBotContext);
  const {
    isISCOPopupShown,
    setIsISCOPopupShown,
    isLaborForcastPopupShown,
    setIsLaborForcastPopupShown,
  } = useContext(ISCOArchetypeContext);
  const {
    sectorsData,
    occupationsData,
    specializationsData,
    setSpecializationsData,
    timelinesData,
    setTimelinesData,
    provinceList,
    jobSourcesData,
    setJobSourcesData,
  } = useContext(ProfileContext);
  const {
    setTopEmployersByISCED4,
    setYearlySpecializationAndSupplyTrend,
    setDemandAndSupplyPercentageByCountry,
    setTopSectorAndOccupationByISCED4,
    setCareerPathwayByISCED4,
    setEducationLevelPercentage,
    setSupplyEducationLevelPercentage,
    setTotalGraduatesAndStudent,
    setSupplyOfGraduatesByISCED4,
    setTopSpecializationsBySupplyJobPostView,
    setTopSpecializationsBySupplyGraduatesView,
    setTopSpecializationsByDemand,
    setTopSpecializationsByDemandJobPostView,
    setTopSpecializationsByDemandGraduatesView,
  } = useContext(EducationCareerContext);
  const {
    setMostRequiredISCO4JobInfo,
    setTopNEmployers,
    setMostRequiredJobInfo,
    setISCO3OccupationList,
    setSectorAndOccupationInfo,
    setTopOccupationsBySectors,
    setYearlyOccupationTrend,
    setTopSkills,
    setTotalJobPosts,
    setMemberMigrationSectors,
    setMemberMigrationOccupations,
  } = useContext(OccupationsContext);
  const {
    setTopEmployersByQuarter,
    setTreeMapInfo,
    setTopSectorsAndOccupationsYearOnYear,
    setUnemployedJobPosts,
  } = useContext(LabourDemandPulseContext);
  const { heatMapData, setHeatMapData } = useContext(HeatMapContext);
  const navigate = useNavigate();

  const [indexOfFromTimeline, setIndexOfFromTimeline] = useState<number>(0);
  const tabsTitles = useMemo(() => {
    return [
      {
        id: 'heatmap',
        label: 'Sector Heatmap',
        display: true,
      },
      {
        id: 'labourDemandPulse',
        label: 'Labour Demand Pulse',
        display: true,
      },
      {
        id: 'educationCareer',
        label: 'Education and Career Paths',
        display: true,
      },
      {
        id: 'occupation',
        label: 'Occupation and Sector Profiles​',
        display: true,
      },
    ];
  }, []);
  const [updatedEvent, setUpdatedEvent] = useState('');
  const [titles, setTitles] = useState<string>('');
  const [educationCareerTitle, setEducationCareerTitle] = useState<string>('');
  //isLoadingTabContent = we added ongoing to be able to detect state changes, when clicking tabs for example, if the the apis are not completly called
  const [isLoadingTabContent, setIsLoadingTabContent] = useState<
    boolean | 'ongoing'
  >(true);

  const [loadingOccupation, setLoadingOccupation] = useState<number>(0);
  const [loadingLabourPulse, setLoadingLabourPulse] = useState<number>(0);
  const [loadingEducationCareer, setLoadingEducationCareer] =
    useState<number>(0);
  const [closeMenu, setCloseMenu] = useState<boolean>(false);

  // Use "tempSelectedProfile" to append all the user filtering selection and then on click "Go" button it will set all the filtering selections into "selectedProfile"
  // to avoid on mouse click/out on any filter to change anything on the UI (only when clicking on "Go" button it will call the APIs)
  const [tempSelectedProfile, setTempSelectedProfile] =
    useState<types.SelectedProfileTypes>({
      provinces: [],
      sector: [],
      occupation: [],
      specializations: [],
      fromTimeline: { title: '', id: '' },
      toTimeline: { title: '', id: '' },
      isEntryLevel: false,
    });

  const [selectedProfile, setSelectedProfile] =
    useState<types.SelectedProfileTypes>({
      provinces: [],
      sector: [],
      occupation: [],
      specializations: [],
      fromTimeline: { title: '', id: '' },
      toTimeline: { title: '', id: '' },
      isEntryLevel: false,
    });

  const [selectedTab, setSelectedTab] = useState<string | number | null>(
    tabsTitles[0]?.id,
  );

  const isChatAIEnabled =
    (process.env.REACT_APP_CHAT_AI_ENABLED &&
      process.env.REACT_APP_CHAT_AI_ENABLED.toLowerCase() === 'true') ||
    false;
  const isISCOArchertpeEnabled =
    (process.env.REACT_APP_CHAT_ISCO_ARCHETYPE_ENABLED &&
      process.env.REACT_APP_CHAT_ISCO_ARCHETYPE_ENABLED.toLowerCase() ===
        'true') ||
    false;

  const handleTabChange = (id: string) => {
    var tempProfile = { ...tempSelectedProfile };
    if (
      id === 'occupation' &&
      tempProfile.occupation &&
      tempProfile.occupation.length
    ) {
      tempProfile.occupation = [];
      setSelectedProfile(tempProfile);
      setTempSelectedProfile(tempProfile);
    } else if (
      id === 'labourDemandPulse' &&
      ((tempProfile.occupation && tempProfile.occupation.length) ||
        (tempProfile.sector && tempProfile.sector.length))
    ) {
      tempProfile.occupation = [];
      tempProfile.sector = [];
      setSelectedProfile(tempProfile);
      setTempSelectedProfile(tempProfile);
    } else if (
      id === 'educationCareer' &&
      timelinesData &&
      timelinesData.length > 0
    ) {
      const defaultEducationTimeline = timelinesData.find(
        x => x.id === 'Q2 2022',
      );
      if (defaultEducationTimeline) {
        var tempTimelinesData: any[] = [];
        var timelinesByHubId = Timelines.find((timeline: any) => {
          return timeline.hubId === countryData.hubId;
        });
        if (timelinesByHubId && timelinesByHubId.timelines)
          tempTimelinesData = timelinesByHubId.timelines;
        else tempTimelinesData = DefaultTimelines;
        setIndexOfFromTimeline(
          tempTimelinesData.indexOf(defaultEducationTimeline.id),
        );

        tempProfile.fromTimeline = {
          title: defaultEducationTimeline.title,
          id: defaultEducationTimeline.id,
        };
        tempProfile.toTimeline = { title: '', id: '' };
        setSelectedProfile(tempProfile);
        setTempSelectedProfile(tempProfile);
      }
    }
    setSelectedTab(id.replace('chartsto-', '')); // remove chartsto- if exists
    setUpdatedEvent('tabUpdated');
    setIsLoadingTabContent(true);
  };

  useEffect(() => {
    if (isMobile) {
      setSelectedTab(null);
    } else {
      setSelectedTab(tabsTitles[0].id);
    }
  }, [isMobile]); // eslint-disable-line

  const countryData = useMemo(() => {
    return (
      countryDetails?.find(({ code }: any) => code === selectedCountry) || null
    );
  }, [selectedCountry, countryDetails]);

  const countryCode = useMemo(
    () => selectedCountry?.toLowerCase() || '',
    [selectedCountry],
  );

  const resetAll = () => {
    setTopNEmployers && setTopNEmployers([]);
    setMostRequiredJobInfo &&
      setMostRequiredJobInfo({
        education_level: '',
        specializations: [],
        experience_range: '',
        total_jobs: 0,
        isco3_job_counts: [],
      });
    setISCO3OccupationList && setISCO3OccupationList([]);
    setYearlyOccupationTrend && setYearlyOccupationTrend([]);
    setMostRequiredISCO4JobInfo && setMostRequiredISCO4JobInfo(null);
    setTopOccupationsBySectors && setTopOccupationsBySectors([]);
    setSectorAndOccupationInfo &&
      setSectorAndOccupationInfo({ sectors: [], occupations: [] });
    setTopSkills && setTopSkills([]);
    setTopEmployersByQuarter && setTopEmployersByQuarter([]);
    setTreeMapInfo && setTreeMapInfo({ sectors: [], occupations: [] });
    setTopSectorsAndOccupationsYearOnYear &&
      setTopSectorsAndOccupationsYearOnYear({
        sectors: [],
        isco2_occupations: [],
        isco4_occupations: [],
      });
    setTopEmployersByISCED4 && setTopEmployersByISCED4([]);
    setYearlySpecializationAndSupplyTrend &&
      setYearlySpecializationAndSupplyTrend({
        specializationtrend: [],
        supplytrend: [],
      });
    setDemandAndSupplyPercentageByCountry &&
      setDemandAndSupplyPercentageByCountry({
        demand_percentage: null,
        supply_percentage: null,
      });
    setEducationLevelPercentage && setEducationLevelPercentage([]);
    setSupplyOfGraduatesByISCED4 &&
      setSupplyOfGraduatesByISCED4({ topFiveSupply: [], total: null });
    setTopSectorAndOccupationByISCED4 &&
      setTopSectorAndOccupationByISCED4({
        sectors: [],
        isco3_occupations: [],
        isco4_occupations: [],
      });
    setCareerPathwayByISCED4 && setCareerPathwayByISCED4([]);
    setTopSpecializationsByDemand && setTopSpecializationsByDemand([]);
    setTopSpecializationsByDemandJobPostView &&
      setTopSpecializationsByDemandJobPostView([]);
    setTopSpecializationsByDemandGraduatesView &&
      setTopSpecializationsByDemandGraduatesView([]);
    setTopSpecializationsBySupplyJobPostView &&
      setTopSpecializationsBySupplyJobPostView([]);
    setTopSpecializationsBySupplyGraduatesView &&
      setTopSpecializationsBySupplyGraduatesView([]);
    setTotalJobPosts && setTotalJobPosts(null);
    setJobSourcesData && setJobSourcesData([]);
  };

  const fetchData = async () => {
    if (
      !specializationsData ||
      (specializationsData && specializationsData.length === 0)
    ) {
      const [specializationsErr, incomingSpecializationsData] =
        await url.getSpecializationList();
      if (specializationsErr) console.warn(specializationsErr);
      if (setSpecializationsData)
        setSpecializationsData(incomingSpecializationsData);
    }

    // Timelines are set statically to improve performance
    var tempTimelinesData: any[] = [];
    // If Timelines contains current hubId then set predefined timelines otherwise use DefaultTimelines
    var timelinesByHubId = Timelines.find((timeline: any) => {
      return timeline.hubId === countryData.hubId;
    });
    if (timelinesByHubId && timelinesByHubId.timelines)
      tempTimelinesData = timelinesByHubId.timelines;
    else tempTimelinesData = DefaultTimelines;
    setTimelinesData &&
      setTimelinesData(
        tempTimelinesData.map((e: string) => ({ id: e, title: e })),
      );

    var tempProfile = { ...tempSelectedProfile };
    const fromTimeline: types.SelectedProfileChildrenTypes =
      updatedEvent !== '' &&
      ((tempProfile.fromTimeline.id &&
        tempTimelinesData.indexOf(tempProfile.fromTimeline.id) > -1) ||
        !tempProfile.fromTimeline.id)
        ? tempProfile.fromTimeline
        : {
            id: tempTimelinesData[tempTimelinesData.length - 1],
            title: tempTimelinesData[tempTimelinesData.length - 1],
          };
    const toTimeline: types.SelectedProfileChildrenTypes =
      updatedEvent !== '' &&
      ((tempProfile.toTimeline.id &&
        tempTimelinesData.indexOf(tempProfile.toTimeline.id) > -1) ||
        !tempProfile.toTimeline.id)
        ? tempProfile.toTimeline
        : {
            id: tempTimelinesData[tempTimelinesData.length - 1],
            title: tempTimelinesData[tempTimelinesData.length - 1],
          };
    tempProfile.fromTimeline = fromTimeline;
    tempProfile.toTimeline = toTimeline;
    setSelectedProfile(tempProfile);
    setTempSelectedProfile(tempProfile);

    setIndexOfFromTimeline(tempTimelinesData.indexOf(fromTimeline.id));

    fetchHeatmapData(fromTimeline.id, toTimeline.id);
  };

  const fetchJobSources = async (
    selectedSectorIds: string[],
    selectedOccupationIds: string[],
    selectedISCED4Codes: string[],
    selectedProvinces: string[],
    selectedFromTimeline: string,
    selectedToTimeline: string,
  ) => {
    const [errJobSources, incomingJobSources] = await url.getJobSources(
      [countryData.hubId],
      selectedSectorIds,
      selectedOccupationIds,
      selectedISCED4Codes,
      selectedProvinces,
      selectedFromTimeline,
      selectedToTimeline,
      selectedProfile.isEntryLevel,
    );
    if (errJobSources && errJobSources === Cancelled) return;
    if (errJobSources) console.warn(errJobSources);
    else setJobSourcesData && setJobSourcesData(incomingJobSources);
  };

  const fetchOccupationData = async (
    selectedFromTimeline: string,
    selectedToTimeline: string,
  ) => {
    const occupationCategoryIds = selectedProfile.occupation.length
      ? selectedProfile.occupation.map((e: any) => e.id)
      : [];
    const sectorIds = selectedProfile.sector.length
      ? selectedProfile.sector.map((e: any) => e.id)
      : [];
    const selectedProvincesObj = selectedProfile.provinces.length
      ? selectedProfile.provinces.map((province: any) => province.title)
      : [];
    if (selectedProfile.occupation.length || selectedProfile.sector.length) {
      const [errTotalJobPosts, incomingTotalJobPosts] =
        await url.getTotalJobPosts(
          [countryData.hubId],
          sectorIds,
          occupationCategoryIds,
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
          selectedProfile.isEntryLevel,
        );
      if (errTotalJobPosts) console.warn(errTotalJobPosts);
      if (setTotalJobPosts) setTotalJobPosts(incomingTotalJobPosts);

      const [errTopNEmployers, incomingTopNEmployers] =
        await url.getTopNEmployers(
          [countryData.hubId],
          sectorIds,
          occupationCategoryIds,
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
          selectedProfile.isEntryLevel,
        );
      if (errTopNEmployers) console.warn(errTopNEmployers);
      if (setTopNEmployers) setTopNEmployers(incomingTopNEmployers);

      const [errYearlyOccupationTrend, incomingYearlyOccupationTrend] =
        await url.getYearlyOccupationTrend(
          [countryData.hubId],
          sectorIds,
          occupationCategoryIds,
          selectedFromTimeline,
          selectedProvincesObj,
          selectedProfile.isEntryLevel,
        );
      if (errYearlyOccupationTrend) console.warn(errYearlyOccupationTrend);
      /////// TO BE REMOVED!!!
      var tempIncomingYearlyOccupationTrendWithSectorAndOccupation: any = [
        ...incomingYearlyOccupationTrend,
      ];
      if (
        tempIncomingYearlyOccupationTrendWithSectorAndOccupation &&
        tempIncomingYearlyOccupationTrendWithSectorAndOccupation.length > 0
      ) {
        tempIncomingYearlyOccupationTrendWithSectorAndOccupation =
          tempIncomingYearlyOccupationTrendWithSectorAndOccupation.map(
            (value: any) => {
              // Statically set the Q3 2022
              if (value.date === 'Q3 2022') {
                var previousQuarter =
                  tempIncomingYearlyOccupationTrendWithSectorAndOccupation.find(
                    (currentValue: any) => currentValue.date === 'Q2 2022',
                  );
                if (previousQuarter)
                  value.value =
                    previousQuarter.value +
                    parseInt((previousQuarter.value * 0.05).toFixed(0));
              }
              return value;
            },
          );
      }
      /////// TO BE REMOVED!!!
      if (setYearlyOccupationTrend)
        setYearlyOccupationTrend(
          tempIncomingYearlyOccupationTrendWithSectorAndOccupation &&
            tempIncomingYearlyOccupationTrendWithSectorAndOccupation.length
            ? tempIncomingYearlyOccupationTrendWithSectorAndOccupation.map(
                (e: any) => ({ name: e.date, value: e.value }),
              )
            : [],
        );

      setLoadingOccupation(1);

      if (
        selectedProfile.sector.length > 0 &&
        selectedProfile.occupation.length === 0
      ) {
        setTitles(
          selectedProfile.sector.length === 1
            ? selectedProfile.sector[0]?.title
            : 'selected sectors',
        );
        const [errTopOccupationsBySectors, incomingTopOccupationsBySectors] =
          await url.getTopOccupationsBySectors(
            [countryData.hubId],
            sectorIds,
            selectedProvincesObj,
            selectedFromTimeline,
            selectedToTimeline,
            selectedProfile.isEntryLevel,
          );
        if (errTopOccupationsBySectors)
          console.warn(errTopOccupationsBySectors);
        else
          setTopOccupationsBySectors &&
            setTopOccupationsBySectors(incomingTopOccupationsBySectors);
      } else setTopOccupationsBySectors && setTopOccupationsBySectors([]);

      setLoadingOccupation(2);

      const [errMostRequiredJobInfo, incomingMostRequiredJobInfo] =
        await url.getMostRequiredJobInfo(
          [countryData.hubId],
          sectorIds,
          occupationCategoryIds,
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
          selectedProfile.isEntryLevel,
        );
      if (errMostRequiredJobInfo) console.warn(errMostRequiredJobInfo);
      else
        setMostRequiredJobInfo &&
          setMostRequiredJobInfo(incomingMostRequiredJobInfo);

      if (selectedProfile.occupation.length > 0) {
        var title =
          selectedProfile.occupation.length === 1
            ? selectedProfile.occupation[0]?.title
            : 'selected occupation(s)';
        if (selectedProfile.sector.length > 0)
          title += ' in selected sector(s)';
        setTitles(title);
        const [errISCO3OccupationList, incomingISCO3OccupationList] =
          await url.getISCO3OccupationList(occupationCategoryIds);
        if (errISCO3OccupationList) console.warn(errISCO3OccupationList);
        else
          setISCO3OccupationList &&
            setISCO3OccupationList(incomingISCO3OccupationList);
      } else {
        setISCO3OccupationList && setISCO3OccupationList([]);

        const [errMostRequiredISCO4JobInfo, incomingMostRequiredISCO4JobInfo] =
          await url.getMostRequiredISCO4JobInfo(
            [countryData.hubId],
            sectorIds,
            occupationCategoryIds,
            [],
            selectedProvincesObj,
            selectedFromTimeline,
            selectedToTimeline,
            selectedProfile.isEntryLevel,
          );
        if (errMostRequiredISCO4JobInfo)
          console.warn(errMostRequiredISCO4JobInfo);
        else
          setMostRequiredISCO4JobInfo &&
            setMostRequiredISCO4JobInfo(incomingMostRequiredISCO4JobInfo);
      }

      setLoadingOccupation(3);
    } else {
      setTitles(countryData.country);
      const [
        errTopSectorAndOccupationInfo,
        incomingTopSectorAndOccupationInfo,
      ] = await url.getTopSectorAndOccupationInfo(
        [countryData.hubId],
        selectedProvincesObj,
        selectedFromTimeline,
        selectedToTimeline,
        selectedProfile.isEntryLevel,
      );
      if (errTopSectorAndOccupationInfo)
        console.warn(errTopSectorAndOccupationInfo);
      else
        setSectorAndOccupationInfo &&
          setSectorAndOccupationInfo(incomingTopSectorAndOccupationInfo);

      setLoadingOccupation(1);

      ////LabourDemandPulseTab
      const [errTopEmployersByQuarter, incomingTopEmployersByQuarter] =
        await url.getTopEmployersByQuarter(
          [countryData.hubId],
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
          selectedProfile.isEntryLevel,
        );
      if (errTopEmployersByQuarter) console.warn(errTopEmployersByQuarter);
      else
        setTopEmployersByQuarter &&
          setTopEmployersByQuarter(incomingTopEmployersByQuarter);

      //const [errYearlyOccupationTrend, incomingYearlyOccupationTrend] = await url.getYearlyOccupationTrend([countryData.hubId], [], [], selectedFromTimeline, selectedProvincesObj, selectedProfile.isEntryLevel);
      //if (errYearlyOccupationTrend) console.warn(errYearlyOccupationTrend);
      ///////// TO BE REMOVED!!!
      //var tempIncomingYearlyOccupationTrend: any = [...incomingYearlyOccupationTrend];
      //if (tempIncomingYearlyOccupationTrend && tempIncomingYearlyOccupationTrend.length > 0) {
      //    tempIncomingYearlyOccupationTrend = tempIncomingYearlyOccupationTrend.map((value: any) => {
      //        // Statically set the Q3 2022
      //        if (value.date === "Q3 2022") {
      //            var previousQuarter = tempIncomingYearlyOccupationTrend.find((currentValue: any) => currentValue.date === "Q2 2022");
      //            if (previousQuarter)
      //                value.value = previousQuarter.value + parseInt((previousQuarter.value * 0.05).toFixed(0));
      //        }
      //        return value;
      //    })
      //}
      ///////// TO BE REMOVED!!!
      //setYearlyOccupationTrend && setYearlyOccupationTrend(tempIncomingYearlyOccupationTrend && tempIncomingYearlyOccupationTrend.length ? tempIncomingYearlyOccupationTrend.map((e: any) => ({ name: e.date, value: e.value })) : [])

      setLoadingLabourPulse(1);

      const [errTreeMapInfo, incomingTreeMapInfo] = await url.getTreeMapInfo(
        [countryData.hubId],
        selectedProvincesObj,
        selectedFromTimeline,
        selectedToTimeline,
        selectedProfile.isEntryLevel,
      );
      if (errTreeMapInfo) console.warn(errTreeMapInfo);
      else setTreeMapInfo && setTreeMapInfo(incomingTreeMapInfo);

      setLoadingLabourPulse(2);

      //const [errTopSectorsAndOccupationsYearOnYear, incomingTopSectorsAndOccupationsYearOnYear] = await url.GetTopSectorAndOccupationYearOnYear([countryData.hubId], selectedProvincesObj, selectedFromTimeline, selectedToTimeline, selectedProfile.isEntryLevel);
      //if (errTopSectorsAndOccupationsYearOnYear) console.warn(errTopSectorsAndOccupationsYearOnYear);
      //else setTopSectorsAndOccupationsYearOnYear && setTopSectorsAndOccupationsYearOnYear(incomingTopSectorsAndOccupationsYearOnYear)

      setLoadingLabourPulse(3);

      const [errUnemployedJobPosts, incomingUnemployedJobPosts] =
        await url.getUnemployedJobPosts([countryData.hubId]);
      if (errUnemployedJobPosts) console.warn(errUnemployedJobPosts);
      else
        setUnemployedJobPosts &&
          setUnemployedJobPosts(incomingUnemployedJobPosts);

      setLoadingLabourPulse(4);
      ////LabourDemandPulseTab
    }

    // Calling Member Migration Sectors
    const [errMemberMigrationSectors, incomingMemberMigrationSectors] =
      await url.getMemberMigrationSectors([countryData.hubId]);
    if (errMemberMigrationSectors) console.warn(errMemberMigrationSectors);
    else
      setMemberMigrationSectors &&
        setMemberMigrationSectors(incomingMemberMigrationSectors);

    setLoadingOccupation(4);

    // Calling Member Migration Occupations
    const [errMemberMigrationOccupations, incomingMemberMigrationOccupations] =
      await url.getMemberMigrationOccupations([countryData.hubId]);
    if (errMemberMigrationOccupations)
      console.warn(errMemberMigrationOccupations);
    else
      setMemberMigrationOccupations &&
        setMemberMigrationOccupations(incomingMemberMigrationOccupations);

    setLoadingOccupation(5);

    //Calling Top  Skills
    const [errTopSkills, incomingTopSkills] = await url.getTopSkills(
      [countryData.hubId],
      sectorIds,
      occupationCategoryIds,
      [],
      selectedProvincesObj,
      selectedProfile.isEntryLevel,
    );
    if (errTopSkills) console.warn(errTopSkills);
    else setTopSkills && setTopSkills(incomingTopSkills);

    setLoadingOccupation(6);
    setLoadingLabourPulse(5);
    setIsLoadingTabContent(false);
  };
  const fetchEducationCareerData = async (
    selectedFromTimeline: string,
    selectedToTimeline: string,
  ) => {
    setEducationCareerTitle(
      selectedProfile.specializations.length === 1
        ? selectedProfile.specializations[0].title
        : 'selected specializations',
    );
    const Isced4Code = selectedProfile.specializations.length
      ? selectedProfile.specializations.map((e: any) => e.id)
      : [];
    const selectedProvincesObj = selectedProfile.provinces.length
      ? selectedProfile.provinces.map((province: any) => province.title)
      : [];

    const fullAccessHubIds = getFullAccessHubIds();

    if (Isced4Code.length > 0) {
      const [errTopEmployersByISCED4, incomingTopEmployersByISCED4] =
        await url.getTopEmployersByISCED4(
          [countryData.hubId],
          Isced4Code,
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
          selectedProfile.isEntryLevel,
        );
      if (errTopEmployersByISCED4) console.warn(errTopEmployersByISCED4);
      if (setTopEmployersByISCED4)
        setTopEmployersByISCED4(incomingTopEmployersByISCED4);

      //const [errYearlySpecializationAndSupplyTrend, incomingYearlySpecializationAndSupplyTrend] = await url.getYearlySpecializationAndSupplyTrend([countryData.hubId], Isced4Code, selectedProvincesObj, selectedProfile.isEntryLevel);
      //if (errYearlySpecializationAndSupplyTrend) console.warn(errYearlySpecializationAndSupplyTrend);
      ///////// TO BE REMOVED!!!
      //var tempIncomingYearlySpecializationAndSupplyTrend: any = { ...incomingYearlySpecializationAndSupplyTrend };
      //if (tempIncomingYearlySpecializationAndSupplyTrend && tempIncomingYearlySpecializationAndSupplyTrend.specializationtrend && tempIncomingYearlySpecializationAndSupplyTrend.specializationtrend.length > 0) {
      //    tempIncomingYearlySpecializationAndSupplyTrend.specializationtrend = tempIncomingYearlySpecializationAndSupplyTrend.specializationtrend.map((value: any) => {
      //        // Statically set the Q3 2022
      //        if (value.date === "Q3 2022") {
      //            var previousQuarter = tempIncomingYearlySpecializationAndSupplyTrend.specializationtrend.find((currentValue: any) => currentValue.date === "Q2 2022");
      //            if (previousQuarter)
      //                value.value = previousQuarter.value + parseInt((previousQuarter.value * 0.05).toFixed(0));
      //        }
      //        return value;
      //    })
      //}
      ///////// TO BE REMOVED!!!
      //if (setYearlySpecializationAndSupplyTrend) setYearlySpecializationAndSupplyTrend(tempIncomingYearlySpecializationAndSupplyTrend)

      const [
        errDemandAndSupplyPercentageByCountry,
        incomingDemandAndSupplyPercentageByCountry,
      ] = await url.getDemandAndSupplyPercentageByCountry(
        [countryData.hubId],
        Isced4Code,
        selectedProvincesObj,
        selectedFromTimeline,
        selectedToTimeline,
        selectedProfile.isEntryLevel,
      );
      if (errDemandAndSupplyPercentageByCountry)
        console.warn(errDemandAndSupplyPercentageByCountry);
      if (setDemandAndSupplyPercentageByCountry)
        setDemandAndSupplyPercentageByCountry(
          incomingDemandAndSupplyPercentageByCountry,
        );
      setLoadingEducationCareer(1);

      const [errEducationLevelPercentage, incomingEducationLevelPercentage] =
        await url.getEducationLevelPercentage(
          [countryData.hubId],
          Isced4Code,
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
          selectedProfile.isEntryLevel,
        );
      if (errEducationLevelPercentage)
        console.warn(errEducationLevelPercentage);
      if (setEducationLevelPercentage)
        setEducationLevelPercentage(incomingEducationLevelPercentage);

      if (fullAccessHubIds && fullAccessHubIds.includes(countryData.hubId)) {
        const [
          errSupplyEducationLevelPercentage,
          incomingSupplyEducationLevelPercentage,
        ] = await url.getSupplyEducationLevelPercentage(
          [countryData.hubId],
          Isced4Code,
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
        );
        if (errSupplyEducationLevelPercentage)
          console.warn(errSupplyEducationLevelPercentage);
        if (setSupplyEducationLevelPercentage)
          setSupplyEducationLevelPercentage(
            incomingSupplyEducationLevelPercentage,
          );

        const [errTotalGraduatesAndStudent, incomingTotalGraduatesAndStudent] =
          await url.getTotalGraduatesAndStudent(
            [countryData.hubId],
            Isced4Code,
            selectedProvincesObj,
            selectedFromTimeline,
            selectedToTimeline,
          );
        if (errTotalGraduatesAndStudent)
          console.warn(errTotalGraduatesAndStudent);
        if (setTotalGraduatesAndStudent)
          setTotalGraduatesAndStudent(incomingTotalGraduatesAndStudent);

        const [
          errSupplyOfGraduatesByISCED4,
          incomingSupplyOfGraduatesByISCED4,
        ] = await url.getSupplyOfGraduatesByISCED4(
          [countryData.hubId],
          Isced4Code,
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
        );
        incomingSupplyOfGraduatesByISCED4 &&
          incomingSupplyOfGraduatesByISCED4.topFiveSupply &&
          incomingSupplyOfGraduatesByISCED4.topFiveSupply.length &&
          incomingSupplyOfGraduatesByISCED4.topFiveSupply.map((value: any) => {
            if (value.institutionType === 'HE') {
              value.institutionType = 'Higher Education';
            }
            return value;
          });
        if (errSupplyOfGraduatesByISCED4)
          console.warn(errSupplyOfGraduatesByISCED4);
        if (setSupplyOfGraduatesByISCED4)
          setSupplyOfGraduatesByISCED4(incomingSupplyOfGraduatesByISCED4);
      } else {
        if (setSupplyEducationLevelPercentage)
          setSupplyEducationLevelPercentage([]);
        if (setTotalGraduatesAndStudent) setTotalGraduatesAndStudent({});
        if (setSupplyOfGraduatesByISCED4)
          setSupplyOfGraduatesByISCED4({ topFiveSupply: [], total: null });
      }

      setLoadingEducationCareer(2);

      const [
        errTopSectorAndOccupationByISCED4,
        incomingTopSectorAndOccupationByISCED4,
      ] = await url.getTopSectorAndOccupationByISCED4(
        [countryData.hubId],
        Isced4Code,
        selectedProvincesObj,
        selectedFromTimeline,
        selectedToTimeline,
        selectedProfile.isEntryLevel,
      );
      if (errTopSectorAndOccupationByISCED4)
        console.warn(errTopSectorAndOccupationByISCED4);
      if (setTopSectorAndOccupationByISCED4)
        setTopSectorAndOccupationByISCED4(
          incomingTopSectorAndOccupationByISCED4,
        );
      setLoadingEducationCareer(3);

      const [errCareerPathwayByISCED4, incomingCareerPathwayByISCED4] =
        await url.getCareerPathwayByISCED4(
          [countryData.hubId],
          Isced4Code,
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
          selectedProfile.isEntryLevel,
        );
      if (errCareerPathwayByISCED4) console.warn(errCareerPathwayByISCED4);
      if (setCareerPathwayByISCED4)
        setCareerPathwayByISCED4(incomingCareerPathwayByISCED4);
      setLoadingEducationCareer(4);
    } else {
      if (fullAccessHubIds && fullAccessHubIds.includes(countryData.hubId)) {
        if (setTopSpecializationsByDemand) setTopSpecializationsByDemand([]);

        const [
          errTopSpecializationsByDemandJobPostView,
          incomingTopSpecializationsByDemandJobPostView,
        ] = await url.getTopSpecializationsByDemandJobPostView(
          [countryData.hubId],
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
        );
        if (errTopSpecializationsByDemandJobPostView)
          console.warn(errTopSpecializationsByDemandJobPostView);
        if (setTopSpecializationsByDemandJobPostView)
          setTopSpecializationsByDemandJobPostView(
            incomingTopSpecializationsByDemandJobPostView,
          );
        setLoadingEducationCareer(1);

        const [
          errTopSpecializationsByDemandGraduatesView,
          incomingTopSpecializationsByDemandGraduatesView,
        ] = await url.getTopSpecializationsByDemandGraduatesView(
          [countryData.hubId],
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
        );
        if (errTopSpecializationsByDemandGraduatesView)
          console.warn(errTopSpecializationsByDemandGraduatesView);
        if (setTopSpecializationsByDemandGraduatesView)
          setTopSpecializationsByDemandGraduatesView(
            incomingTopSpecializationsByDemandGraduatesView,
          );
        setLoadingEducationCareer(2);

        const [
          errTopSpecializationsBySupplyJobPostView,
          incomingTopSpecializationsBySupplyJobPostView,
        ] = await url.getTopSpecializationsBySupplyJobPostView(
          [countryData.hubId],
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
        );
        if (errTopSpecializationsBySupplyJobPostView)
          console.warn(errTopSpecializationsBySupplyJobPostView);
        if (setTopSpecializationsBySupplyJobPostView)
          setTopSpecializationsBySupplyJobPostView(
            incomingTopSpecializationsBySupplyJobPostView,
          );
        setLoadingEducationCareer(3);

        const [
          errTopSpecializationsBySupplyGraduatesView,
          incomingTopSpecializationsBySupplyGraduatesView,
        ] = await url.getTopSpecializationsBySupplyGraduatesView(
          [countryData.hubId],
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
        );
        if (errTopSpecializationsBySupplyGraduatesView)
          console.warn(errTopSpecializationsBySupplyGraduatesView);
        if (setTopSpecializationsBySupplyGraduatesView)
          setTopSpecializationsBySupplyGraduatesView(
            incomingTopSpecializationsBySupplyGraduatesView,
          );
      } else {
        if (setTopSpecializationsByDemandJobPostView)
          setTopSpecializationsByDemandJobPostView([]);
        if (setTopSpecializationsByDemandGraduatesView)
          setTopSpecializationsByDemandGraduatesView([]);
        if (setTopSpecializationsBySupplyJobPostView)
          setTopSpecializationsBySupplyJobPostView([]);
        if (setTopSpecializationsBySupplyGraduatesView)
          setTopSpecializationsBySupplyGraduatesView([]);

        const [
          errTopSpecializationsByDemand,
          incomingTopSpecializationsByDemand,
        ] = await url.getTopSpecializationsByDemand(
          [countryData.hubId],
          selectedProvincesObj,
          selectedFromTimeline,
          selectedToTimeline,
        );
        if (errTopSpecializationsByDemand)
          console.warn(errTopSpecializationsByDemand);
        if (setTopSpecializationsByDemand)
          setTopSpecializationsByDemand(incomingTopSpecializationsByDemand);
        setLoadingEducationCareer(1);
      }
      setLoadingEducationCareer(4);
    }

    //Calling Top  Skills
    const [errTopSkills, incomingTopSkills] = await url.getTopSkills(
      [countryData.hubId],
      [],
      [],
      Isced4Code,
      selectedProvincesObj,
      selectedProfile.isEntryLevel,
    );
    if (errTopSkills) console.warn(errTopSkills);
    else setTopSkills && setTopSkills(incomingTopSkills);

    setLoadingEducationCareer(5);
    setIsLoadingTabContent(false);
  };
  const fetchHeatmapData = async (
    selectedFromTimeline: string,
    selectedToTimeline: string,
  ) => {
    const selectedProvincesObj = selectedProfile.provinces.length
      ? selectedProfile.provinces.map((province: any) => province.title)
      : [];
    const [heatMapErr, incomingHeatMapData] = await url.getHeatMapInfo(
      [countryData.hubId],
      selectedProvincesObj,
      selectedFromTimeline,
      selectedToTimeline,
      selectedProfile.isEntryLevel,
    );
    if (heatMapErr && heatMapErr === Cancelled) return;
    if (heatMapErr) {
      console.warn(heatMapErr);
    }
    setHeatMapData && setHeatMapData(incomingHeatMapData);

    setIsLoadingTabContent(false);
  };

  const fetchISCOData = async () => {
    setIsISCOPopupShown && setIsISCOPopupShown(true);

    setIsLoadingTabContent(false);
  };

  useEffect(() => {
    // Default selection for heatmap" all occupations and all sectors and latest quarter
    if (
      heatMapData &&
      heatMapData.length &&
      timelinesData &&
      timelinesData.length &&
      updatedEvent === ''
    ) {
      //const sortedHeatmap = heatMapData && heatMapData.sort((a: any, b: any) => b.value - a.value)
      //const sortedSectorsFromHeatmap = [...new Set(sortedHeatmap.map((e: any) => e.sector))] //from heatmap data

      //const sortedSectors = sortedSectorsFromHeatmap.slice(0, 5)
      //const top5Sectors = sectorsData?.filter((e: any) => sortedSectors.includes(e.title))

      var tempProfile = {
        provinces: [],
        occupation: occupationsData || [],
        sector: sectorsData || [],
        specializations: [],
        fromTimeline: timelinesData[timelinesData.length - 1],
        toTimeline: timelinesData[timelinesData.length - 1],
        isEntryLevel: false,
      };
      setSelectedProfile(tempProfile);
      setTempSelectedProfile(tempProfile);
      setIndexOfFromTimeline(timelinesData.length - 1);

      fetchJobSources(
        sectorsData?.map((e: any) => e.id) || [],
        occupationsData?.map((e: any) => e.id) || [],
        [],
        [],
        timelinesData[timelinesData.length - 1].title,
        timelinesData[timelinesData.length - 1].title,
      );
    }
  }, [heatMapData, timelinesData, updatedEvent]); // eslint-disable-line

  const handleClickChartToOccupationTab = (selectedSquare: any) => {
    setIsLoadingTabContent('ongoing');
    const occupation = selectedSquare.occupation
      ? occupationsData?.find((e: any) => e.title === selectedSquare.occupation)
      : null;
    const sector = selectedSquare.sector
      ? sectorsData?.find((e: any) => e.title === selectedSquare.sector)
      : null;
    var tempProfile = { ...tempSelectedProfile };
    tempProfile.occupation = occupation ? [occupation] : [];
    tempProfile.sector = sector ? [sector] : [];
    setSelectedProfile(tempProfile);
    setTempSelectedProfile(tempProfile);
    handleTabChange('chartsto-occupation');
  };

  useEffect(() => {
    if (countryData) {
      if (isISCOArchertpeEnabled) {
        fetchISCOData();
      } else {
        fetchData();
      }
    }

    return () => {
      setSelectedCountry && setSelectedCountry(null);
      setGlobePosition && setGlobePosition(null);
    };
  }, [
    countryData,
    isISCOArchertpeEnabled,
    setSelectedCountry,
    setGlobePosition,
  ]); // eslint-disable-line

  useEffect(() => {
    if (countryData) {
      setIsLoadingTabContent(true);

      // fetchCountries();
    }
  }, [countryData]); // eslint-disable-line

  useEffect(() => {
    if (isLoadingTabContent === true && updatedEvent !== '') {
      resetAll();
      setLoadingOccupation(0);
      setLoadingLabourPulse(0);
      setLoadingEducationCareer(0);
      callApis();
      setIsLoadingTabContent('ongoing');
      setUpdatedEvent('updated');
    }
  }, [isLoadingTabContent]); // eslint-disable-line

  const callApis = () => {
    const fromTimelineTitle = selectedProfile.fromTimeline?.title;
    const toTimelineTitle = selectedProfile.toTimeline?.title;

    switch (selectedTab) {
      case 'heatmap':
        fetchHeatmapData(fromTimelineTitle, toTimelineTitle);
        fetchJobSources(
          selectedProfile.sector.map((e: any) => e.id),
          selectedProfile.occupation.map((e: any) => e.id),
          [],
          selectedProfile.provinces.map((e: any) => e.title),
          fromTimelineTitle,
          toTimelineTitle,
        );
        break;
      case 'labourDemandPulse':
        fetchOccupationData(fromTimelineTitle, toTimelineTitle);
        fetchJobSources(
          [],
          [],
          [],
          selectedProfile.provinces.map((e: any) => e.title),
          fromTimelineTitle,
          toTimelineTitle,
        );
        break;
      case 'educationCareer':
        fetchEducationCareerData(fromTimelineTitle, toTimelineTitle);
        fetchJobSources(
          [],
          [],
          selectedProfile.specializations.map((e: any) => e.id),
          selectedProfile.provinces.map((e: any) => e.title),
          fromTimelineTitle,
          toTimelineTitle,
        );
        break;
      case 'occupation':
        fetchOccupationData(fromTimelineTitle, toTimelineTitle);
        fetchJobSources(
          selectedProfile.sector.map((e: any) => e.id),
          selectedProfile.occupation.map((e: any) => e.id),
          [],
          selectedProfile.provinces.map((e: any) => e.title),
          fromTimelineTitle,
          toTimelineTitle,
        );
        break;
    }
  };

  const handleChangeMultipleSelect = (
    event: any,
    currentTitle: string,
    currentParentTitle: string,
    data: types.SelectedProfileChildrenTypes[],
  ) => {
    const { value, name } = event.target;
    if (currentTitle === 'reset') {
      setTempSelectedProfile((prevState: types.SelectedProfileTypes) => ({
        ...prevState,
        [name]: [],
      }));
      return;
    }

    if (currentTitle === 'all') {
      var selectedData = data.filter(item => item.id);
      setTempSelectedProfile((prevState: types.SelectedProfileTypes) => ({
        ...prevState,
        [name]: selectedData,
      }));
    } else {
      const selectedProfileDefinedIndex: { [index: string]: any } =
        tempSelectedProfile;
      const selectedItems = selectedProfileDefinedIndex[name];
      var itemAlreadySelected: any;
      var selectedItemsValue: any;
      if (currentTitle) {
        itemAlreadySelected = selectedItems
          .map(function (e: any) {
            return e.title;
          })
          .indexOf(currentTitle);
        selectedItemsValue =
          itemAlreadySelected !== -1
            ? selectedItems.filter((e: any) => e.title !== currentTitle)
            : value;
      } else {
        if (currentParentTitle) {
          itemAlreadySelected =
            selectedItems &&
            selectedItems.length > 0 &&
            data &&
            data.length > 0 &&
            selectedItems.filter(
              (e: any) => e.parentTitle === currentParentTitle,
            ).length ===
              data.filter(
                (e: any) => e.id && e.parentTitle === currentParentTitle,
              ).length
              ? 1
              : -1;
          var selectedItemsValueWithoutParentRelated: any =
            selectedItems.filter(
              (e: any) => e.parentTitle !== currentParentTitle,
            );
          selectedItemsValue =
            itemAlreadySelected !== -1
              ? selectedItemsValueWithoutParentRelated
              : selectedItemsValueWithoutParentRelated.concat(
                  data.filter(
                    (e: any) => e.title && e.parentTitle === currentParentTitle,
                  ),
                );
        }
      }

      setTempSelectedProfile((prevState: types.SelectedProfileTypes) => ({
        ...prevState,
        [name]:
          typeof selectedItemsValue === 'string' ? [] : selectedItemsValue,
      }));
    }
  };

  const handleSelectSpecializationsFromChart = (
    selected: types.TopSpecializationsByDemandTypes,
  ) => {
    setUpdatedEvent('specializations-from-chart');
    var tempProfile = { ...tempSelectedProfile };
    const specializationToBeSelected =
      specializationsData &&
      specializationsData.filter(
        (value: types.SelectedProfileChildrenTypes) => {
          return value.parentId === selected.isced3Code && value.id != null;
        },
      );

    if (specializationToBeSelected) {
      tempProfile.specializations = specializationToBeSelected;
      setSelectedProfile(tempProfile);
      setTempSelectedProfile(tempProfile);
    }

    setIsLoadingTabContent(true);
  };

  const handleChangeTimeline = (event: any) => {
    if (!timelinesData) return;

    let profileKey: string;
    let profileResult: types.SelectedProfileChildrenTypes = {
      title: '',
      id: '',
    };
    if (event.target === 'reset') {
      profileKey = event.name;
      profileResult = { title: '', id: '' };
    } else {
      const { value, name } = event.target;
      const selectedObj = timelinesData.length
        ? timelinesData.find((e: any) => e.id === value)
        : null;
      profileKey = name;
      profileResult = selectedObj ? selectedObj : { title: '', id: '' };
    }
    if (profileKey === 'fromTimeline') {
      setTempSelectedProfile((prevState: any) => ({
        ...prevState,
        [profileKey]: profileResult,
        toTimeline: { title: '', id: '' },
      }));
      setIndexOfFromTimeline(timelinesData.indexOf(profileResult));
    } else {
      setTempSelectedProfile((prevState: any) => ({
        ...prevState,
        [profileKey]: profileResult,
      }));
    }
  };

  const handleChangeIsEntryLevel = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setTempSelectedProfile((prevState: any) => ({
      ...prevState,
      isEntryLevel: event.target.checked,
    }));
  };

  const handleClose = useCallback(() => {
    onClose && onClose();
  }, [onClose]);

  const handleCloseMenu = () => {
    setCloseMenu(!closeMenu);
  };

  const runFetchData = useCallback(() => {
    setUpdatedEvent('set-selected-profile');
    setSelectedProfile({ ...tempSelectedProfile });
    setIsLoadingTabContent(true);
  }, [tempSelectedProfile]);

  const openChatBot = () => {
    setConversationId && conversationId === '' && setConversationId(uuidv4());
    setIsOpenChatModal(true);
  };

  const handleCloseChatModal = useCallback(() => {
    setConversationId && setConversationId('');
    setIsOpenChatModal(false);
    setTempRequestSearch &&
      setTempRequestSearch([
        {
          name: 'Whiteshield',
          placeholder: null,
          loading: false,
          text: "Hello! I'm the AI Minister, a job market expert here to assist you with your inquiries. How can I help you today?",
          textar: '',
          lang: 'en',
          showIcons: false,
          chart: null,
          stopConversation: false,
        },
      ]);
  }, [setConversationId, setTempRequestSearch]);
  const handleMouseEnter = () => setIsChatHovered(true);
  const handleMouseLeave = () => setIsChatHovered(false);

  const handleGoBackToCitizenView = useCallback(() => {
    setIsISCOPopupShown && setIsISCOPopupShown(true);
  }, [setIsISCOPopupShown]);

  const handleLaborForcastView = useCallback(() => {
    setIsLaborForcastPopupShown && setIsLaborForcastPopupShown(true);
  }, [setIsLaborForcastPopupShown]);

  return (
    <>
      {createPortal(
        isISCOPopupShown && isISCOArchertpeEnabled ? (
          <ISCOArchetypesComponent
            fetchData={fetchData}
            countryCode={countryCode}
            setSelectedProfile={setTempSelectedProfile}
          />
        ) : isLaborForcastPopupShown ? (
          <LaborForecast />
        ) : (
          <div className="country-details p-2">
            <div
              className={`country-details__profile-form ${
                closeMenu && 'country-details__profile-form--hide-menu'
              }`}
            >
              <div className="country-details__header align-items-bottom mt-2 mb-4">
                <CountryFlag countryCode={countryCode} />
                <SelectCountry setSelectedProfile={setTempSelectedProfile} />
              </div>

              {selectedTab === 'heatmap' ||
              selectedTab === 'occupation' ||
              selectedTab === 'labourDemandPulse' ||
              selectedTab === 'educationCareer' ? (
                <div
                  className="country-details-filters"
                  data-tour="risk-filters"
                >
                  <div
                    className={`tabs-left-content ${
                      isLoadingTabContent === true && 'loading'
                    }`}
                  >
                    {selectedCountry &&
                    provinceList &&
                    provinceList.length > 0 ? (
                      <div className="mb-4">
                        <SelectMultiple
                          title="provinces"
                          name="provinces"
                          selectedItems={tempSelectedProfile.provinces}
                          data={provinceList}
                          handleChange={handleChangeMultipleSelect}
                          style={{ minWidth: 360 }}
                          disabled={
                            provinceList?.filter(
                              (item: any) => item.title !== 'Unspecified',
                            ).length === 0
                              ? true
                              : false
                          }
                        />
                      </div>
                    ) : (
                      <></>
                    )}
                    {selectedTab === 'heatmap' ||
                    selectedTab === 'occupation' ? (
                      <>
                        <div className="mb-4">
                          <SelectMultiple
                            title="sectors"
                            name="sector"
                            selectedItems={tempSelectedProfile.sector}
                            data={sectorsData}
                            handleChange={handleChangeMultipleSelect}
                            style={{ minWidth: 360 }}
                          />
                        </div>
                        <div className="mb-4">
                          <SelectMultiple
                            title="occupations"
                            name="occupation"
                            selectedItems={tempSelectedProfile.occupation}
                            data={occupationsData}
                            handleChange={handleChangeMultipleSelect}
                            displayTooltip={true}
                            prefixTooltip="Includes: "
                            style={{ minWidth: 360 }}
                          />
                        </div>
                      </>
                    ) : null}
                    {selectedTab === 'educationCareer' ? (
                      <>
                        <div className="mb-4">
                          <SelectMultiple
                            title="specializations"
                            name="specializations"
                            selectedItems={tempSelectedProfile.specializations}
                            data={specializationsData}
                            handleChange={handleChangeMultipleSelect}
                            style={{ minWidth: 360 }}
                          />
                        </div>
                      </>
                    ) : null}
                    {timelinesData && timelinesData.length > 0 && (
                      <div className="mb-2 row">
                        <div className="col-6">
                          <SelectSingle
                            title="From"
                            name="fromTimeline"
                            selectedItem={
                              tempSelectedProfile.fromTimeline
                                ? tempSelectedProfile.fromTimeline.id
                                : ''
                            }
                            data={timelinesData}
                            handleChange={handleChangeTimeline}
                          />
                        </div>
                        <div className="col-6">
                          <SelectSingle
                            title="To"
                            name="toTimeline"
                            selectedItem={
                              tempSelectedProfile.toTimeline
                                ? tempSelectedProfile.toTimeline.id
                                : ''
                            }
                            data={timelinesData?.filter(
                              (e, index) => index >= indexOfFromTimeline,
                            )}
                            handleChange={handleChangeTimeline}
                          />
                        </div>
                      </div>
                    )}
                    <div className="mb-2 country-details__action-buttons">
                      {selectedTab !== 'educationCareer' ||
                      (selectedTab === 'educationCareer' &&
                        tempSelectedProfile.specializations.length > 0) ? (
                        <FormControlLabel
                          className="switch-entry-level-label"
                          control={
                            <Switch
                              className="switch-entry-level"
                              checked={tempSelectedProfile.isEntryLevel}
                              onChange={handleChangeIsEntryLevel}
                            />
                          }
                          label="Entry Level"
                        />
                      ) : null}
                      <button
                        type="button"
                        onClick={runFetchData}
                        className="button button_with-icon"
                      >
                        Run{' '}
                        <ArrowForwardIos
                          style={{ width: 30, float: 'right' }}
                        />
                      </button>
                    </div>
                    {isChatAIEnabled && (
                      <div
                        onClick={openChatBot}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        className="d-flex justify-content-center align-items-center slidingChatIcon"
                      >
                        <div
                          className={`pe-3 ${
                            isChatHovered ? 'd-block' : 'd-none'
                          }`}
                        >
                          <Typewriter text="Chat With AI Minister" delay={20} />
                        </div>
                        <img
                          src={AIMinisterIcon}
                          alt="AI Minister"
                          width={40}
                          height={60}
                        />
                      </div>
                    )}
                    <div
                      className={'summary-selection-container ' + selectedTab}
                    >
                      {selectedTab === 'occupation' ? (
                        <>
                          {tempSelectedProfile.sector &&
                            tempSelectedProfile.sector.length > 0 && (
                              <div className="summary-selection">
                                <div className="summary-selection-title">
                                  Sector(s)
                                </div>
                                <div
                                  className={cn('summary-selection-values', {
                                    medium:
                                      tempSelectedProfile.occupation.length ===
                                      0,
                                  })}
                                >
                                  {tempSelectedProfile.sector.length ===
                                  sectorsData?.length ? (
                                    <div>All</div>
                                  ) : (
                                    tempSelectedProfile.sector.map(
                                      (e: any, index) => {
                                        return <div key={index}>{e.title}</div>;
                                      },
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                          {tempSelectedProfile.occupation &&
                            tempSelectedProfile.occupation.length > 0 && (
                              <div className="summary-selection">
                                <div className="summary-selection-title">
                                  Occupation(s)
                                </div>
                                <div
                                  className={cn('summary-selection-values', {
                                    medium:
                                      tempSelectedProfile.sector.length === 0,
                                  })}
                                >
                                  {tempSelectedProfile.occupation.length ===
                                  occupationsData?.length ? (
                                    <div>All</div>
                                  ) : (
                                    tempSelectedProfile.occupation.map(
                                      (e: any, index) => {
                                        return <div key={index}>{e.title}</div>;
                                      },
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                        </>
                      ) : null}
                      {selectedTab === 'educationCareer' ? (
                        <>
                          {tempSelectedProfile.specializations &&
                            tempSelectedProfile.specializations.length > 0 && (
                              <div className="summary-selection">
                                <div className="summary-selection-title">
                                  Specialization(s)
                                </div>
                                <div className="summary-selection-values long">
                                  {tempSelectedProfile.specializations
                                    .length === specializationsData?.length ? (
                                    <div>All</div>
                                  ) : (
                                    tempSelectedProfile.specializations.map(
                                      (e: any, index) => {
                                        return <div key={index}>{e.title}</div>;
                                      },
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                        </>
                      ) : null}
                    </div>
                  </div>
                  {
                    // isLoadingTabContent === true ? <img src={LoaderImage} alt="" className="loadingImg" /> : <></>
                  }
                </div>
              ) : null}
              <div className="country-details-bottom-container">
                {process.env.REACT_APP_LABOUR_FORECAST_ENABLED === 'true' && (
                  <button
                    type="button"
                    onClick={handleLaborForcastView}
                    className="button button_with-icon button-isco-laborForcast"
                  >
                    Labour Forecast{' '}
                    <TrendingUpIcon
                      style={{
                        width: 30,
                        float: 'right',
                        fontSize: '90px',
                        marginRight: '16px',
                      }}
                    />
                  </button>
                )}
                {isISCOArchertpeEnabled && (
                  <>
                    <button
                      type="button"
                      onClick={handleGoBackToCitizenView}
                      className="button button_with-icon button-isco-archetypes"
                    >
                      Citizen View{' '}
                      <GroupsIcon
                        style={{
                          width: 30,
                          float: 'right',
                          fontSize: '90px',
                          marginRight: '16px',
                        }}
                      />
                    </button>
                    <hr />
                  </>
                )}
                {jobSourcesData && jobSourcesData.length > 0 ? (
                  <>
                    <div className="sources">
                      {jobSourcesData.map((value, index) => {
                        return (
                          <Tooltip
                            id="tooltip-source"
                            key={'tooltip' + index}
                            title={value.count}
                            placement="top"
                          >
                            <span id={value.source}>
                              {value.source +
                                ' (' +
                                value.percentage +
                                '%)' +
                                (index === jobSourcesData.length - 1
                                  ? ''
                                  : ',')}
                            </span>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </>
                ) : null}
              </div>
            </div>

            <div
              className={`country-details__content position-relative ${
                closeMenu && 'country-details__content--hide-menu'
              }`}
            >
              {!closeMenu ? (
                <ArrowCircleLeft
                  type="button"
                  onClick={handleCloseMenu}
                  className="country-details__close-menu-button"
                />
              ) : (
                <ArrowCircleRight
                  type="button"
                  onClick={handleCloseMenu}
                  className="country-details__close-menu-button"
                />
              )}
              <Cancel
                type="button"
                onClick={handleClose}
                className="country-details__close-button"
              />
              {countryData ? (
                <Tabs
                  tabs={tabsTitles}
                  selectedTab={selectedTab}
                  contentStyle={{
                    height: `calc(100vh - ${isMobile ? 62 : 172}px)`,
                    overflowX: 'auto',
                  }}
                  onChange={handleTabChange}
                >
                  {selectedTab === 'occupation' ? (
                    <OccupationTab
                      countryData={countryData}
                      selectedProfile={selectedProfile}
                      titles={titles}
                      isLoading={loadingOccupation}
                    />
                  ) : null}
                  {selectedTab === 'heatmap' ? (
                    <HeatMapTab
                      selectedProfile={selectedProfile}
                      isLoadingTabContent={isLoadingTabContent}
                      handleClick={handleClickChartToOccupationTab}
                    />
                  ) : null}
                  {selectedTab === 'labourDemandPulse' ? (
                    <LabourDemandPulseTab
                      countryData={countryData}
                      isLoading={loadingLabourPulse}
                      handleClick={handleClickChartToOccupationTab}
                      selectedProfile={selectedProfile}
                    />
                  ) : null}
                  {selectedTab === 'educationCareer' ? (
                    <EducationCareerTab
                      title={educationCareerTitle}
                      isLoading={loadingEducationCareer}
                      handleSelectSpecializationsFromChart={
                        handleSelectSpecializationsFromChart
                      }
                      countryData={countryData}
                      selectedProfile={selectedProfile}
                    />
                  ) : null}
                </Tabs>
              ) : null}
            </div>
            {isChatAIEnabled && (
              <ChatModal
                isOpen={isOpenChatModal}
                onClose={handleCloseChatModal}
              />
            )}
          </div>
        ),
        document.body,
      )}
    </>
  );
};

export default CountryDetails;
